import { useRef, useState } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { uploadImage, fileUrl } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import ImageLightbox from "@/components/ImageLightbox";

const MAX_BYTES = 10 * 1024 * 1024;

export default function ImageAttacher({ images = [], onChange, size = "md", compact = false, triggerRef = null }) {
  const { t } = useT();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const inputRef = useRef(null);

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    const paths = [];
    for (const file of list) {
      if (!file.type.startsWith("image/")) {
        toast.error(t("images.wrongType"));
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(t("images.tooLarge"));
        continue;
      }
      try {
        const { path } = await uploadImage(file);
        paths.push(path);
      } catch (e) {
        toast.error(t("images.failed"));
      }
    }
    setUploading(false);
    if (paths.length) onChange([...(images || []), ...paths]);
  };

  const remove = (p) => {
    onChange((images || []).filter((x) => x !== p));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length) addFiles(files);
  };

  const thumbSize = size === "sm" ? "h-16 w-16" : "h-20 w-20";

  return (
    <div className="space-y-2" data-testid="image-attacher">
      {(images && images.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {images.map((p, i) => (
            <div key={p} className={cn("relative group rounded-xl overflow-hidden border border-stone-200 dark:border-stone-700 cursor-zoom-in", thumbSize)}>
              <img
                src={fileUrl(p)}
                alt=""
                className="w-full h-full object-cover"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx(i); }}
                data-testid={`image-thumb-${i}`}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(p); }}
                aria-label={t("images.remove")}
                data-testid={`image-remove-${p.split("/").pop()}`}
                className="absolute top-1 right-1 h-5 w-5 inline-flex items-center justify-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => { if (!compact) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="image-drop"
        className={cn(
          "flex items-center justify-center gap-2 border-2 border-dashed rounded-xl text-sm px-3 py-2.5 cursor-pointer transition-colors",
          dragging
            ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-200"
            : "border-stone-300/70 dark:border-stone-700 text-stone-500 dark:text-stone-400 hover:border-stone-400 dark:hover:border-stone-600",
          // IMPORTANT: `hidden` must come LAST so tailwind-merge keeps it
          // when it conflicts with `flex` on the same element.
          compact && "hidden"
        )}
      >
        {uploading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t("images.uploading")}</>
        ) : (
          <><ImagePlus className="h-4 w-4" />{dragging ? t("images.dropActive") : t("images.drop")}</>
        )}
      </div>

      <input
        ref={(el) => { inputRef.current = el; if (triggerRef) triggerRef.current = el; }}
        type="file"
        accept="image/*"
        multiple
        hidden
        data-testid="image-file-input"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
      />

      <ImageLightbox
        images={images}
        index={lightboxIdx}
        onIndexChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </div>
  );
}
